/**
 * HelpPage — the in-app Help, by role (Pedro 2026-10-11, session 30; first version
 * #15, 2026-06-17). Reached via the Help link pinned at the bottom of the sidebar.
 *
 * The guides are Markdown files bundled at build time (src/help/*.md, imported `?raw`)
 * and rendered by components/MarkdownLite — the text lives in one place and is edited
 * as plain Markdown. Who sees what:
 *   - every role: the Day-to-day guide (HELP_RM.md), written for RMs / Staff;
 *     Read-only users see the same pages, the guide says what they lack;
 *   - Admin: also the Admin guide (HELP_ADMIN.md) — roles, permissions, the Compliance
 *     area, the Admin area, procedures;
 *   - Compliance: also the Compliance guide (HELP_COMPLIANCE.md) — the Admin guide
 *     without the Admin area.
 * RMs are not shown the Admin-only procedures (Pedro: "RMs do not need to see the
 * detail of procedures and records restricted to Admins"). The guides are under Beta
 * test; users' feedback refines the wording.
 */
import { Anchor, Container, Paper, SimpleGrid, Stack, Tabs, Text, Title } from '@mantine/core';
import { useState } from 'react';

import { MarkdownLite, renderInline, slugify } from '../components/MarkdownLite';
import { useCurrentUser } from '../hooks/useAuth';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import adminGuide from '../help/HELP_ADMIN.md?raw';
import complianceGuide from '../help/HELP_COMPLIANCE.md?raw';
import dayToDayGuide from '../help/HELP_RM.md?raw';

/** The guide's "## " sections, as a clickable contents list. */
function Contents({ source }: { source: string }) {
  const sections = source.split('\n')
    .filter((l) => l.startsWith('## '))
    .map((l) => l.slice(3).trim());
  if (sections.length === 0) return null;
  return (
    <Paper withBorder p="sm" radius="md">
      <Text fw={600} size="sm" mb={4}>Contents</Text>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={2} verticalSpacing={2}>
        {sections.map((s) => (
          <Anchor key={s} size="sm" component="button" ta="left"
            onClick={() => document.getElementById(slugify(s))?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
            {renderInline(s)}
          </Anchor>
        ))}
      </SimpleGrid>
    </Paper>
  );
}

function Guide({ source }: { source: string }) {
  return (
    <Stack gap="xs">
      <Contents source={source} />
      <MarkdownLite source={source} />
    </Stack>
  );
}

export function HelpPage() {
  useDocumentTitle('Help');
  const { user } = useCurrentUser();
  const roleGuide = user?.role === 'admin'
    ? { label: 'Admin guide', source: adminGuide }
    : user?.role === 'compliance'
      ? { label: 'Compliance guide', source: complianceGuide }
      : null;
  const [tab, setTab] = useState<string | null>('role');

  return (
    <Container size="md">
      <Stack gap="md">
        <Title order={2}>Help</Title>
        {roleGuide ? (
          // keepMounted off: only the open guide is in the page, so the Contents links
          // never scroll to a heading in the hidden one.
          <Tabs value={tab} onChange={setTab} keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab value="role">{roleGuide.label}</Tabs.Tab>
              <Tabs.Tab value="daily">Day-to-day guide</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="role" pt="md"><Guide source={roleGuide.source} /></Tabs.Panel>
            <Tabs.Panel value="daily" pt="md"><Guide source={dayToDayGuide} /></Tabs.Panel>
          </Tabs>
        ) : (
          <Guide source={dayToDayGuide} />
        )}
      </Stack>
    </Container>
  );
}
